import {
  PartnerIntegrations,
  ShowNotificationFunctionProps,
} from 'lib-integrations-partner-widgets';
import { useHasAccess } from 'lib-shared-access-control';
import { config } from 'lib-shared-backend-config';
import {
  MetaConversation,
  NotificationPayloadT,
} from 'lib-shared-legacy-types';
import { useCallback, useEffect, useState } from 'react';

import { notificationActions } from 'components/notifications/actions/notification';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { usePartnerWidgetFeatureFlags } from 'utils/partnerIntegrationsWidget/usePartnerWidgetFeatureFlags';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { CustomCards } from '../../cards/customCards/customCards';
import type {
  SidebarCardId,
  SidebarMode,
} from '../../conversationSidebar.types';
import { useCardVisibility } from '../../hooks/useCardVisibility';
import { partnerIntegrationNotificationTimeout } from '../../partnerIntegrations';
import { CardSection } from '../cardSection/cardSection';
import { VisibilityToggle } from '../visibilityToggle/visibilityToggle';

type IntegrationCardsProps = {
  metaConversation: MetaConversation;
  isMinimized: boolean;
  isCardOpen: (card: string) => boolean;
  toggleCard: (card: string) => void;
  sidebarMode: SidebarMode;
  index: number;
};

export const IntegrationCards = ({
  metaConversation,
  isMinimized,
  isCardOpen,
  toggleCard,
  sidebarMode,
  index,
}: IntegrationCardsProps): JSX.Element | null => {
  const [hiddenCardsSnapshot, setHiddenCardsSnapshot] = useState(
    new Set<SidebarCardId>(),
  );
  const { hiddenCards } = useCardVisibility();

  const dispatch = useAppDispatch();
  const flags = useFeatureFlag([
    'partner-integrations-widget',
    'conversation-right-sidebar-visibility',
  ]);

  const hasIntegrationAccess = useHasAccess('conversation.integration');

  const partnerWidgetFeatureFlags = usePartnerWidgetFeatureFlags();

  const showNotification = useCallback(
    ({ title, text, icon, severity }: ShowNotificationFunctionProps) => {
      const time =
        severity === 'warning' || severity === 'danger'
          ? 0
          : partnerIntegrationNotificationTimeout;
      const className = severity as string;

      const payload: NotificationPayloadT = {
        title,
        text,
        icon,
        className,
        time,
      };
      dispatch(notificationActions.notificationAdd(payload));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (sidebarMode === 'visibility') {
      setHiddenCardsSnapshot(hiddenCards);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarMode]);

  if (!hasIntegrationAccess) {
    return null;
  }

  const title = flags.conversationRightSidebarVisibility
    ? 'Native integrations'
    : 'Integrations';

  return (
    <CardSection
      title={!isMinimized ? title : undefined}
      index={index}
      isMinimized={isMinimized}
    >
      {flags.partnerIntegrationsWidget && (
        <PartnerIntegrations
          configuration={{
            baseUrl: config.url.partnerIntegrations,
            featureFlags: partnerWidgetFeatureFlags,
          }}
          conversationContext={{
            conversationId: metaConversation.conversation?.id || null,
            agent:
              metaConversation?.conversation?.assignment?.agent ?? undefined,
            customer: metaConversation?.requester ?? undefined,
          }}
          showNotification={showNotification}
          onToggleFn={toggleCard}
          isOpenFn={isCardOpen}
          isMinimized={isMinimized}
          getVisibilityProperties={(key) => ({
            isHidden: sidebarMode !== 'visibility' && hiddenCards.has(key),
            toggler: <VisibilityToggle cardId={key} />,
          })}
          sortFn={(a, b, keyA, keyB) => {
            if (sidebarMode === 'visibility') {
              return (
                Number(hiddenCardsSnapshot.has(keyB)) -
                  Number(hiddenCardsSnapshot.has(keyA)) ||
                a.name.localeCompare(b.name)
              );
            }
            return a.name.localeCompare(b.name);
          }}
        />
      )}

      {!flags.conversationRightSidebarVisibility && (
        <CustomCards
          isMinimized={isMinimized}
          csid={metaConversation.conversation?.id}
          sidebarMode="default"
        />
      )}
    </CardSection>
  );
};
